import { ExtractUrlParams, withParams, withQuery } from "@/utils/url"
import { object, string, z } from "zod"
import { APIError } from "./errors"
import { reportSaveDataSchema, reportSaveSchema } from "@/state/report"
import axios from "axios"

function endpoint<
	Url extends string,
	Res,
	P extends ExtractUrlParams<Url>,
	Q extends {} = {},
	B extends {} = {}
>(
	method: RequestInit["method"],
	url: Url,
	responseSchema: z.ZodType<Res>,
	querySchema?: z.ZodType<Q>,
	bodySchema?: z.ZodType<B>
) {
	type _P = keyof P extends never ? {} : { params: P }
	type _Q = keyof Q extends never ? {} : { query: Q }
	type _B = keyof B extends never ? {} : { body: B }
	type Opts = _P & _Q & _B
	return async (opts: Opts, init?: RequestInit) => {
		// unforunate workaround for TS not being able to infer the type of opts
		let { query, body, params } = Object.assign(
			{ query: undefined, body: undefined, params: undefined },
			opts
		)

		// validate and prepare
		querySchema?.parse(query)
		bodySchema?.parse(body)

		let u = withQuery(
			withParams(url, params as any /* unfortunately, but safe */),
			query || {}
		)

		// check init and set headers
		if (!init) init = {}

		if (!init.method) init.method = method

		let headers = new Headers(init.headers)
		let contentType = headers.get("Content-Type")
		init.headers = headers

		if (!contentType && body) {
			contentType = "application/json"
			headers.set("Content-Type", contentType)
			init.headers = headers
			init.body = JSON.stringify(body)
		}

		const res = await fetch(u, init)
		const data = await res.json()

		if (!res.ok) {
			return Promise.reject(new APIError(res.status, res.statusText, data))
		}

		const parsed = responseSchema.safeParse(data)
		if (!parsed.success) {
			return Promise.reject(new APIError(500, "Failed to parse response", parsed.error))
		}
		return parsed.data
	}
}

function get<Url extends string, Res, P extends ExtractUrlParams<Url>, Q extends {}>(
	url: Url,
	validators: { response: z.ZodType<Res>; query?: z.ZodType<Q> }
) {
	return endpoint<Url, Res, P, Q>("GET", url, validators.response, validators.query)
}
function post<
	Url extends string,
	Res,
	P extends ExtractUrlParams<Url>,
	Q extends {},
	B extends {}
>(
	url: Url,
	validators: { response: z.ZodType<Res>; query?: z.ZodType<Q>; body?: z.ZodType<B> }
) {
	return endpoint<Url, Res, P, Q, B>(
		"POST",
		url,
		validators.response,
		validators.query,
		validators.body
	)
}
function put<
	Url extends string,
	Res,
	P extends ExtractUrlParams<Url>,
	Q extends {},
	B extends {}
>(
	url: Url,
	validators: { response: z.ZodType<Res>; query?: z.ZodType<Q>; body?: z.ZodType<B> }
) {
	return endpoint<Url, Res, P, Q, B>(
		"PUT",
		url,
		validators.response,
		validators.query,
		validators.body
	)
}

export const Api = {
	createCheckoutSession: get("/api/create-checkout-session", {
		query: z.object({
			months: z.coerce.number(),
			currency: z.string(),
			payment_method: z.string(),
			report_id: z.string(),
			productName: z.string(),
			return_url: z.string(),
		}),
		response: object({
			payment_url: string(),
		}),
	}),
	createReport: post("/api/report", {
		response: z.object({
			id: z.string(),
			key: z.string(),
		}),
	}),
	getReport: get("/api/report", {
		query: z.object({
			report_id: z.string(),
			key: z.string(),
		}),
		response: reportSaveSchema,
	}),
	updateReport: put("/api/report", {
		body: z
			.object({
				report_id: z.string(),
				key: z.string(),
			})
			.merge(reportSaveDataSchema),
		response: z.object({
			success: z.boolean(),
		}),
	}),
	acceptTerms: post("/api/accept-terms", {
		body: z.object({
			report_id: z.string(),
			key: z.string(),
		}),
		response: z.object({
			success: z.boolean(),
		}),
	}),
	getCheckoutSession: get("/api/get-checkout-status", {
		query: z.object({
			session_id: z.string(),
		}),
		response: z.object({
			report_id: z.string(),
			customer_email: z.string(),
			payment_status: z.enum(["paid", "unpaid"]),
		}),
	}),
	uploadFiles: (body: UploadFiles) => {
		uploadFilesSchema.parse(body)

		const formData = new FormData()
		formData.append("key", body.key)
		formData.append("report_id", body.report_id)
		body.files.forEach(file => {
			formData.append("files", file)
		})

		return axios.post<{
			status: string
			urls: string[]
		}>("/api/s3/upload-files", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		})
	},
}

const uploadFileSchema = z.object({
	report_id: z.string(),
	key: z.string(),
	file: z.instanceof(File),
})

const uploadFilesSchema = z.object({
	report_id: z.string(),
	key: z.string(),
	files: z.array(z.instanceof(File)),
})

type UploadFile = z.infer<typeof uploadFileSchema>
type UploadFiles = z.infer<typeof uploadFilesSchema>
